import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { orcanosItemUrl } from '../api/orcanosClient';
import { psApi, fmtMoney, fmtQty, statusTone } from './psApi';
import PsExportModal from './PsExportModal';
import { KindIcon } from '../tree/icons';
import { AddChildModal, BaselineModal, MoveModal, NewBranchModal, WhereUsedModal, useConfirm } from './PsModals';
import PsBranchPicker from './PsBranchPicker';
import { BranchCompareModal } from './PsCompare';
import { PsBtnIcon } from './psIcons';
import { HistoryIcon, HistoryModal, LockModal, MoreMenu, fmtStamp } from './PsHistory';
import ColumnPicker from '../tree/ColumnPicker';
import { mergeRowColumns } from '../utils/columns';
import '../bom-design-system.css';
import '../tree/BomTree.css';

const Chevron = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true" style={{ transform: `rotate(${open ? 90 : 0}deg)` }}>
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const AllChevron = ({ up }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true"><path d={up ? 'M6 15l6-6 6 6' : 'M6 9l6 6 6-6'} /></svg>
);

const ICONS = {
  child: <><path d="M6 4v9a3 3 0 0 0 3 3h5" /><path d="M18 13v6M15 16h6" /></>,
  used: <><circle cx="12" cy="12" r="7" /><circle cx="12" cy="12" r="2.5" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" /></>,
  move: <><path d="M12 3v18M3 12h18" /><path d="M9 6l3-3 3 3M9 18l3 3 3-3M6 9l-3 3 3 3M18 9l3 3-3 3" /></>,
  shared: <><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></>,
  remove: <><path d="M4 7h16M10 11v6M14 11v6" /><path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3" /></>,
  open: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></>,
  compare: <><path d="M7 4v13M4 14l3 3 3-3" /><path d="M17 20V7M14 10l3-3 3 3" /></>,
  export: <><path d="M12 3v12M7 10l5 5 5-5" /><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /></>,
  lock: <><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>,
  unlock: <><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 7.5-2" /></>,
};

/** The small lock beside a key (2026-11-01): locked here, inside a locked part, or locked in another product. */
const LockMark = ({ tone, title }) => (
  <span className={'ps-lock ps-lock--' + tone} title={title} aria-label={title} role="img">
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true">{ICONS.lock}</svg>
  </span>
);

const rowKind = (r) => (r.type === 'DMS' ? 'doc' : r.type === 'CR' ? 'eco' : (r.has_kids || r.depth === 0 ? 'asm' : 'part'));

/** A row action drawn as an icon; the label is its tooltip and accessible name. */
const MenuIcon = ({ icon }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true">{ICONS[icon]}</svg>
);

/** Row ⋮ menu (2026-11-02): less-used row actions as icon + text. Drawn in a portal at a fixed
 *  spot — the grid scrolls inside itself and would clip an absolute menu. */
const RowMenu = ({ items }) => {
  const list = items.filter(Boolean);
  const [pos, setPos] = useState(null);
  const btn = useRef(null);
  const menu = useRef(null);
  useEffect(() => {
    if (!pos) return undefined;
    const close = (e) => { if (!menu.current?.contains(e.target) && !btn.current?.contains(e.target)) setPos(null); };
    const key = (e) => { if (e.key === 'Escape') setPos(null); };
    const off = () => setPos(null);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', key);
    window.addEventListener('scroll', off, true);
    window.addEventListener('resize', off);
    return () => {
      document.removeEventListener('mousedown', close); document.removeEventListener('keydown', key);
      window.removeEventListener('scroll', off, true); window.removeEventListener('resize', off);
    };
  }, [pos]);
  if (!list.length) return null;
  const toggle = () => {
    if (pos) { setPos(null); return; }
    const r = btn.current.getBoundingClientRect();
    const up = window.innerHeight - r.bottom < 46 * list.length + 24;
    setPos({ right: window.innerWidth - r.right, ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
  };
  return (
    <>
      <button ref={btn} type="button" className={'ps-ibtn' + (pos ? ' ps-ibtn--on' : '')} aria-haspopup="menu" aria-expanded={!!pos}
        aria-label="More actions" title="More actions" onClick={toggle}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="5" r="1.9" /><circle cx="12" cy="12" r="1.9" /><circle cx="12" cy="19" r="1.9" /></svg>
      </button>
      {pos && createPortal(
        <div ref={menu} className="ps-more-menu ps-row-menu" role="menu" style={pos}>
          {list.map((it) => (
            <button key={it.label} type="button" role="menuitem" className={'ps-more-item' + (it.danger ? ' ps-more-item--danger' : '')}
              title={it.hint} onClick={() => { setPos(null); it.onClick(); }}>
              <MenuIcon icon={it.icon} /><span><b>{it.label}</b></span>
            </button>
          ))}
        </div>, document.body)}
    </>
  );
};

export const IconBtn = ({ icon, label, onClick, danger, tone }) => (
  <button type="button" className={'ps-ibtn' + (danger ? ' ps-ibtn--danger' : '') + (tone ? ' ps-ibtn--' + tone : '')} title={label} aria-label={label} onClick={onClick}>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true">{ICONS[icon]}</svg>
  </button>
);

// ─── Columns (2026-11-02, like the BOM Viewer's picker) ─────────────────────
// The structure's own columns first (shown by default), then every Orcanos field the
// rows' filters return — part, assembly and DMS rows merged by Name or Title, so a field
// both types carry is one column. Item and the action column are always shown.
const BUILTIN_COLS = [
  { key: '__type', title: 'Type', width: '48px', px: 48 },
  { key: '__rev', title: 'Rev', width: '44px', px: 44, tip: 'Revision — read live from Orcanos' },
  { key: '__status', title: 'Status', width: '96px', px: 96 },
  { key: '__qty', title: 'Qty', width: '84px', px: 84 },
  { key: '__find', title: 'Find', width: '60px', px: 60, tip: 'Find number — the balloon number that points to this part on the assembly drawing' },
  { key: '__refdes', title: 'Ref. des.', width: 'minmax(90px, 0.8fr)', px: 90, tip: 'Reference designators — where the part sits on the board, e.g. R1, R5, C12' },
  { key: '__unit', title: 'Unit cost', width: '84px', px: 84, num: true },
  { key: '__line', title: 'Line cost', width: '96px', px: 96, num: true },
];
const BUILTIN_KEYS = BUILTIN_COLS.map((c) => c.key);
// Fields the Item column already shows, or that mean nothing on a row.
const FIELD_HIDDEN = new Set(['key', 'user prefix', 'name', 'obj name', 'copy as link', 'in pool', 'is branch',
  'original id', 'id', 'master part source']);
const COLS_PREF = 'ps_tree_cols';

function loadColsPref() {
  try {
    const v = JSON.parse(localStorage.getItem(COLS_PREF) || 'null');
    return Array.isArray(v) ? new Set(v) : null;
  } catch { return null; }
}

/** A row's text for a merged field column — every Name, then every Title; tags stripped. */
function fieldValue(r, col) {
  const fs = r.fields || [];
  const f = col.names.map((n) => fs.find((x) => x.Name === n)).find((x) => x?.Display_text)
    || col.titles.map((t) => fs.find((x) => x.Title === t)).find((x) => x?.Display_text);
  return f ? f.Display_text.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim() : '';
}

/** A rk is "root/linkId/linkId…": its ancestors are its prefixes. */
const parentRk = (rk) => rk.slice(0, rk.lastIndexOf('/'));

function QtyCell({ value, disabled, onSave }) {
  const [v, setV] = useState(fmtQty(value));
  useEffect(() => setV(fmtQty(value)), [value]);
  const commit = () => {
    const n = parseFloat(v);
    if (!(n > 0)) { setV(fmtQty(value)); return; }
    if (n !== Number(value)) onSave(n);
  };
  if (disabled) return <span>{fmtQty(value)}</span>;
  return (
    <input className="ps-in ps-in--qty" type="number" min="0" step="any" aria-label="Quantity" value={v}
      onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
  );
}

/** Unit cost of a leaf part — written to Orcanos (POST /cost), allowed while locked (2026-11-02). */
function CostCell({ value, onSave }) {
  const [v, setV] = useState(String(value ?? 0));
  useEffect(() => setV(String(value ?? 0)), [value]);
  const commit = () => {
    const n = parseFloat(v);
    if (!(n >= 0)) { setV(String(value ?? 0)); return; }
    if (Math.abs(n - Number(value || 0)) > 0.00001) onSave(n);
  };
  return (
    <input className="ps-in ps-in--cost" type="number" min="0" step="any" aria-label="Unit cost" title="Unit cost — saved in Orcanos"
      value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
  );
}

function TextCell({ value, disabled, label, onSave }) {
  const [v, setV] = useState(value || '');
  useEffect(() => setV(value || ''), [value]);
  if (disabled) return <span className="ps-small">{value}</span>;
  return (
    <input className="ps-in" aria-label={label} value={v} onChange={(e) => setV(e.target.value)}
      onBlur={() => { if (v !== (value || '')) onSave(v); }} onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
  );
}

export default function PsTree({ root, branchId, locate, onLocate, onBranch, onOpen, showToast, onAuth }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState({});
  const [branches, setBranches] = useState([]);
  const [modal, setModal] = useState(null);
  const [drag, setDrag] = useState(null);        // { row, bad: Set<node> } while a line is dragged
  const [dropRk, setDropRk] = useState('');
  const locRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [t, b] = await Promise.all([psApi.tree(root, branchId || undefined), psApi.branches(root)]);
      setData(t);
      setBranches(b.branches);
      setError('');
    } catch (e) {
      if (!onAuth(e)) setError(e.message);
    }
  }, [root, branchId, onAuth]);

  useEffect(() => { setData(null); setOpen({ [root]: true }); load(); }, [root, branchId, load]);

  // Locate: open every ancestor of the located rows, then scroll to the first one.
  useEffect(() => {
    if (!locate || !data) return;
    const next = { ...open };
    data.rows.filter((r) => r.node === locate).forEach((r) => {
      let p = parentRk(r.rk);
      while (p) { next[p] = true; p = p.includes('/') ? parentRk(p) : ''; }
    });
    setOpen(next);
    setTimeout(() => locRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 50);
  }, [locate, data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search (2026-11-01): key, name or item id over the WHOLE structure, not just the open levels.
  // A hit is shown with its ancestors (faded) so the path still reads; open/closed is ignored meanwhile.
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const search = useMemo(() => {
    if (!data || !needle) return null;
    const hits = new Set(data.rows
      .filter((r) => `${r.key || ''} ${r.name || ''} ${r.id ?? ''} ${r.node ?? ''}`.toLowerCase().includes(needle))
      .map((r) => r.rk));
    const keep = new Set(hits);
    hits.forEach((rk) => { let p = rk.includes('/') ? parentRk(rk) : ''; while (p) { keep.add(p); p = p.includes('/') ? parentRk(p) : ''; } });
    return { hits, keep, parts: new Set(data.rows.filter((r) => hits.has(r.rk)).map((r) => r.node)).size };
  }, [data, needle]);

  // Only changes (2026-11-01): in a branch, just the lines added / changed there, with their ancestors.
  const [onlyChg, setOnlyChg] = useState(false);
  useEffect(() => { setOnlyChg(false); }, [branchId]);
  const chgKeep = useMemo(() => {
    if (!data?.branch || !onlyChg) return null;
    const keep = new Set();
    data.rows.filter((r) => r.branch_added || r.branch_changed).forEach((r) => {
      keep.add(r.rk);
      let p = r.rk.includes('/') ? parentRk(r.rk) : '';
      while (p) { keep.add(p); p = p.includes('/') ? parentRk(p) : ''; }
    });
    return keep;
  }, [data, onlyChg]);

  const visible = useMemo(() => {
    if (!data) return [];
    if (chgKeep) return data.rows.filter((r) => chgKeep.has(r.rk) && (!search || search.keep.has(r.rk)));
    if (search) return data.rows.filter((r) => search.keep.has(r.rk));
    return data.rows.filter((r) => {
      let p = r.rk.includes('/') ? parentRk(r.rk) : '';
      while (p) {
        if (!open[p]) return false;
        p = p.includes('/') ? parentRk(p) : '';
      }
      return true;
    });
  }, [data, open, search, chgKeep]);

  // Columns picker: the structure's own columns + every Orcanos field of the loaded rows.
  const columns = useMemo(() => {
    const fieldCols = data
      ? mergeRowColumns(data.rows.map((r) => r.fields), { skip: (n, t) => FIELD_HIDDEN.has(n) || FIELD_HIDDEN.has(t) })
        .map((c) => ({ ...c, key: 'f:' + c.key, width: 'minmax(120px, 1fr)', px: 120 }))
      : [];
    return [...BUILTIN_COLS, ...fieldCols];
  }, [data]);
  const [colKeys, setColKeys] = useState(loadColsPref);
  const shown = useMemo(() => {
    const chosen = colKeys ? columns.filter((c) => colKeys.has(c.key)) : [];
    return chosen.length ? chosen : columns.filter((c) => BUILTIN_KEYS.includes(c.key));
  }, [columns, colKeys]);
  const shownKeys = useMemo(() => new Set(shown.map((c) => c.key)), [shown]);
  const changeCols = useCallback((next) => {
    setColKeys(next);
    try { localStorage.setItem(COLS_PREF, JSON.stringify([...next])); } catch { /* not kept — fine */ }
  }, []);
  // Item 360 + actions 186 + each column, 10px gap each, 28px side padding.
  const gridStyle = useMemo(() => ({
    gridTemplateColumns: ['minmax(360px, 2.6fr)', ...shown.map((c) => c.width), '186px'].join(' '),
    minWidth: 360 + 186 + shown.reduce((n, c) => n + c.px + 10, 0) + 10 + 28,
  }), [shown]);

  const [confirmEl, ask] = useConfirm();
  const inBranch = !!data?.branch;
  const canEdit = !!data?.can_edit;
  // Add child = Orcanos A on PRT, line fields = U (2026-11-01); an older server sends no can_add.
  const canAdd = data?.can_add ?? canEdit;
  const canDelete = data?.can_delete !== false;   // Orcanos D on PRT; unknown = allowed (server checks too)
  const run = async (fn, msg) => {
    try {
      await fn();
      if (msg) showToast(msg);
      await load();
    } catch (e) {
      if (!onAuth(e)) showToast(e.message, 'error');
    }
  };

  // Copy once (2026-11-01): the product's children in Orcanos (parent_original_id), all levels, become lines here.
  const [copying, setCopying] = useState(false);
  const copyFromOrcanos = async () => {
    setCopying(true);
    try {
      const r = await psApi.copyOrcanos(root);
      if (!r.links) showToast(r.message, 'error');
      else {
        showToast(`Copied ${r.links} line${r.links === 1 ? '' : 's'} (${r.items} items) from Orcanos`
          + (r.kept ? ` · ${r.kept} sub-assembl${r.kept === 1 ? 'y' : 'ies'} already defined here kept as they are` : '')
          + (r.loops ? ` · ${r.loops} loop${r.loops === 1 ? '' : 's'} skipped` : ''));
        await load();
      }
    } catch (e) {
      if (!onAuth(e)) showToast(e.message, 'error');
    } finally {
      setCopying(false);
    }
  };

  const setQty = (r, qty) => run(() => (inBranch
    ? psApi.change(branchId, r.branch_change != null
      ? { op: 'qty', parent: r.parent, child: r.node, qty, replace_index: r.branch_change }
      : { op: 'qty', parent: r.parent, child: r.node, link_id: r.link_id, qty })
    : psApi.patchLink(r.link_id, { root, qty })));
  const setText = (r, field, value) => run(() => (inBranch
    ? psApi.change(branchId, r.branch_change != null
      ? { op: 'edit', parent: r.parent, child: r.node, [field]: value, replace_index: r.branch_change }
      : { op: 'edit', parent: r.parent, child: r.node, link_id: r.link_id, [field]: value })
    : psApi.patchLink(r.link_id, { root, [field]: value })));
  const remove = async (r) => {
    const parentKey = data.rows.find((x) => x.node === r.parent)?.key || '';
    if (!(await ask({
      title: `Remove ${r.key}?`,
      message: `Remove ${r.key} from ${parentKey}${inBranch ? ' in this branch' : ''}? The part stays in Orcanos.`,
      confirmLabel: 'Remove',
    }))) return;
    run(() => (inBranch
      ? psApi.change(branchId, r.branch_change != null
        ? { op: 'remove', parent: r.parent, child: r.node, replace_index: r.branch_change }
        : { op: 'remove', parent: r.parent, child: r.node, link_id: r.link_id })
      : psApi.deleteLink(r.link_id, root)), `Removed ${r.key} from ${parentKey}.`);
  };
  const add = (parentRow) => async ({ child, qty, find_no, ref_des, label }) => {
    if (inBranch) await psApi.change(branchId, { op: 'add', parent: parentRow.node, child, qty, find_no, ref_des });
    else await psApi.addLink({ root, parent: parentRow.node, child, qty, find_no, ref_des });
    setModal(null);
    setOpen((o) => ({ ...o, [parentRow.rk]: true }));
    showToast(`Added ${label} × ${fmtQty(qty)} under ${parentRow.key}.`);
    await load();
  };
  // Lock / unlock a part in this product (2026-11-01): main only, Edit (U) on PRT; the server refuses
  // every main write inside a lock, and a merge that touches one. A dialog says what it means and
  // takes an optional comment, kept with the lock and in the History log (2026-11-02).
  const toggleLock = (r) => setModal({ kind: 'lock', row: r, unlock: !!r.locked });
  const doLock = async (r, unlock, comment) => {
    if (unlock) await psApi.unlock(root, r.node, comment);
    else await psApi.lock(root, r.node, comment);
    setModal(null);
    showToast(unlock ? `${r.key} is unlocked — it can be changed again.`
      : `${r.key} is locked for changes. Cost, baselines and branches are still allowed.`);
    await load();
  };
  const setCost = (r, cost) => run(() => psApi.setCost(root, r.node, cost), `Unit cost of ${r.key} set to ${fmtMoney(cost)} in Orcanos.`);
  const lockWho = (r) => [r.locked_by && ` by ${r.locked_by}`, r.locked_at && ` on ${fmtStamp(r.locked_at)}`].filter(Boolean).join('');
  const lockOf = (r) => {
    const why = r.lock_comment ? `\n“${r.lock_comment}”` : '';
    if (r.locked) return { tone: 'own', title: `Locked${lockWho(r)} — this part and everything under it can't be changed${why}` };
    if (r.lock_inherited) {
      let up = null;
      if (!r.lock_node_key) {
        let p = parentRk(r.rk);
        while (p && !up) { up = rowByRk[p]?.locked ? rowByRk[p] : null; p = p.includes('/') ? parentRk(p) : ''; }
      }
      return { tone: 'in', title: `Locked — inside ${r.lock_node_key || up?.key || 'a locked part'}${lockWho(r)}${why}` };
    }
    if (r.locked_in) return { tone: 'other', title: `Locked in ${r.locked_in} — a shared sub-assembly, so it can't be changed here either` };
    return null;
  };
  const whereUsed = async (r) => {
    try { setModal({ kind: 'wu', data: await psApi.whereUsed(r.node) }); } catch (e) { if (!onAuth(e)) showToast(e.message, 'error'); }
  };
  const moveTargets = (r) => {
    const sub = new Set(data.rows.filter((x) => x.rk === r.rk || x.rk.startsWith(r.rk + '/')).map((x) => x.node));
    const seen = new Set();
    return data.rows.filter((x) => x.type !== 'DMS' && !x.missing && !seen.has(x.node) && seen.add(x.node))
      .map((x) => ({ node: x.node, key: x.key, name: x.name,
        disabled: sub.has(x.node) || x.node === r.parent,
        note: sub.has(x.node) ? 'Inside this line' : x.node === r.parent ? 'Current parent' : '' }));
  };

  // Drag a line onto another assembly = Move (same rules as the Move window: main only, never
  // under itself or anything inside it, never onto a document or a missing item).
  const startDrag = (e, r) => {
    const bad = new Set(data.rows.filter((x) => x.rk === r.rk || x.rk.startsWith(r.rk + '/')).map((x) => x.node));
    bad.add(r.parent);
    setDrag({ row: r, bad });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', r.key);
    const rowEl = e.currentTarget.closest('.ps-trow');
    if (rowEl) e.dataTransfer.setDragImage(rowEl, 20, 20);
  };
  const endDrag = () => { setDrag(null); setDropRk(''); };
  const canDrop = (t) => !!drag && t.type !== 'DMS' && !t.missing && !drag.bad.has(t.node) && !t.kids_locked;
  const dropOn = async (t) => {
    const r = drag.row;
    endDrag();
    if (t.shared > 1 && !window.confirm(`${t.key} is shared by ${t.shared} products — all of them get ${r.key}. Move it there?`)) return;
    await run(() => psApi.moveLink(r.link_id, root, t.node), `Moved ${r.key} under ${t.key}.`);
    setOpen((o) => ({ ...o, [t.rk]: true }));
  };

  if (error) return <div className="ps-error" role="alert">{error}</div>;
  if (!data) return <div className="ps-muted">Loading…</div>;

  const firstLocated = locate ? visible.find((r) => r.node === locate)?.rk : null;
  const expandAll = () => setOpen(Object.fromEntries(data.rows.map((r) => [r.rk, true])));
  const collapseAll = () => setOpen({ [root]: true });
  const rootRow = data.rows[0];
  const rowByRk = Object.fromEntries(data.rows.map((r) => [r.rk, r]));
  const anyLock = data.rows.some((r) => r.locked || r.lock_inherited || r.locked_in);

  return (
    <>
      <div className="ps-toolbar">
        <div className="ps-row">
          <span className="ps-lbl">View</span>
          <PsBranchPicker branches={branches} branchId={branchId} onBranch={onBranch} canEdit={canEdit}
            onNew={(from) => setModal({ kind: 'branch', from })} onManage={onOpen ? () => onOpen('ps-branches') : null} />
          <input className="ps-search ps-search--tree" type="search" placeholder="Search key, name or ID" aria-label="Search this structure"
            value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Escape') setQ(''); }} />
          {search && <span className="ps-muted ps-small">{search.parts
            ? `${search.parts} part${search.parts === 1 ? '' : 's'} · ${search.hits.size} place${search.hits.size === 1 ? '' : 's'}`
            : 'No match'}</span>}
        </div>
        <div className="ps-row" style={{ marginLeft: 'auto' }}>
          {/* Rolled-up cost is the root row's Line cost, so no separate label (user, 2026-11-02). */}
          <div className="bom-scope ps-colpick">
            <ColumnPicker columns={columns} selected={shownKeys} onChange={changeCols} defaultKeys={BUILTIN_KEYS} />
          </div>
          {/* The BOM Viewer's Export window: format, view, columns, with files (2026-11-01). */}
          <button type="button" className="ps-btn ps-btn--ghost ps-btn--sm" onClick={() => setModal({ kind: 'export' })}><PsBtnIcon name="export" />Export…</button>
          {/* The selected product's own pages + Take baseline live in ⋮, icons kept (user, 2026-11-02). */}
          <MoreMenu items={[
            onOpen && { label: 'Baselines', hint: 'Baselines of this product', icon: <PsBtnIcon name="baselines" />, onClick: () => onOpen('ps-baselines') },
            onOpen && { label: 'Branches', hint: 'Branches of this product', icon: <PsBtnIcon name="branches" />, onClick: () => onOpen('ps-branches') },
            !inBranch && canEdit && { label: 'Take baseline', hint: 'Freeze this structure as it is now', icon: <PsBtnIcon name="takeBaseline" />, onClick: () => setModal({ kind: 'baseline' }) },
            { label: 'History log', hint: 'Every change to this structure', icon: <HistoryIcon />, onClick: () => setModal({ kind: 'history' }) },
          ]} />
        </div>
      </div>
      {inBranch && (
        <div className="ps-banner ps-banner--br">
          <b>Branch: {data.branch.name}</b>
          <span>· changes against main are shaded blue. Edits here stay in the branch until it is merged.</span>
          {anyLock && <span>Locked parts can be changed here, but the branch can't be merged until they are unlocked on main.</span>}
          {data.conflicts.length > 0 && <span className="ps-st ps-st--bad">{data.conflicts.length} change(s) no longer fit main — see Branches.</span>}
          <label className="ps-small" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}
            title="Show only the lines added or changed in this branch (with their parents). Removed lines are listed in Compare.">
            <input type="checkbox" checked={onlyChg} onChange={(e) => setOnlyChg(e.target.checked)} />Only changes
          </label>
          <button type="button" className="ps-btn ps-btn--ghost ps-btn--sm" onClick={() => setModal({ kind: 'compare' })}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true" style={{ marginRight: 6, verticalAlign: '-3px' }}>{ICONS.compare}</svg>Compare with…
          </button>
          <button type="button" className="ps-btn ps-btn--ghost ps-btn--sm" onClick={() => onBranch('')}>Back to main</button>
        </div>
      )}
      {inBranch && onlyChg && chgKeep && chgKeep.size === 0 && (
        <p className="ps-muted" style={{ margin: '8px 0' }}>No lines added or changed in this branch. Removed lines show in <b>Compare with…</b>.</p>
      )}
      {locate && (
        <div className="ps-banner ps-banner--loc">
          <span>Showing <b>{data.rows.find((r) => r.node === locate)?.key || locate}</b> — every place it is used is highlighted. Click a highlighted row to clear it.</span>
          <button type="button" className="ps-btn ps-btn--ghost ps-btn--sm" onClick={() => onLocate('')}>Clear</button>
        </div>
      )}
      {rootRow && rootRow.depth === 0 && data.rows.length === 1 && (
        <div className="ps-banner ps-banner--note">
          <span>This product has no parts yet. Use the <b>Add child</b> icon on its row to add the first ones
            {canEdit && !inBranch && <>, or copy the structure it already has in Orcanos</>}.</span>
          {canEdit && !inBranch && (
            <button type="button" className="ps-btn ps-btn--sm" disabled={copying} onClick={copyFromOrcanos}>
              {copying ? 'Copying…' : 'Copy structure from Orcanos'}
            </button>
          )}
        </div>
      )}
      <div className="ps-tw">
        <div className="ps-thead ps-g-tree" style={gridStyle}>
          <span>Item</span>
          {shown.map((c) => <span key={c.key} className={c.num ? 'ps-num' : undefined} title={c.tip || c.title}>{c.title}</span>)}
          <span />
        </div>
        {visible.map((r) => {
          const located = locate && r.node === locate;
          const hit = search?.hits.has(r.rk);
          const isOpen = search ? true : !!open[r.rk];
          // A lock freezes main only — in a branch the line may still be proposed (merge is refused).
          const lineFree = inBranch || !r.line_locked;
          const kidsFree = inBranch || !r.kids_locked;
          const lineEdit = canEdit && r.depth > 0 && lineFree;
          const href = r.missing ? null : orcanosItemUrl({ type: r.type, itemId: r.id });
          const draggable = canEdit && !inBranch && r.depth > 0 && lineFree;
          const lk = lockOf(r);
          const target = drag && canDrop(r);
          return (
            <div key={r.rk} ref={r.rk === firstLocated ? locRef : undefined} style={gridStyle}
              className={'ps-trow ps-g-tree' + (r.depth === 0 ? ' ps-trow--root' : '')
                + ((r.branch_added || r.branch_changed) ? ' ps-trow--chg' : '') + (located ? ' ps-trow--loc' : '')
                + (hit ? ' ps-trow--hit' : '') + (search && !hit ? ' ps-trow--faded' : '')
                + (drag?.row.rk === r.rk ? ' ps-trow--dragging' : '') + (dropRk === r.rk ? ' ps-trow--drop' : '')
                + (drag && !target && drag.row.rk !== r.rk ? ' ps-trow--nodrop' : '')}
              onDragOver={target ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dropRk !== r.rk) setDropRk(r.rk); } : undefined}
              onDragLeave={target ? (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDropRk((k) => (k === r.rk ? '' : k)); } : undefined}
              onDrop={target ? (e) => { e.preventDefault(); dropOn(r); } : undefined}>
              <div className="ps-cell-item" style={{ paddingLeft: r.depth * 24 }}>
                {draggable
                  ? <span className="ps-grip" draggable onDragStart={(e) => startDrag(e, r)} onDragEnd={endDrag}
                      title="Drag onto another assembly to move it there" aria-label={`Drag ${r.key}`}>⠿</span>
                  : canEdit && !inBranch && <span className="ps-grip ps-grip--sp" />}
                {r.has_kids
                  ? <button type="button" className="ps-tog" aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${r.key}`}
                      onClick={() => setOpen((o) => ({ ...o, [r.rk]: !isOpen }))}><Chevron open={isOpen} /></button>
                  : <span className="ps-tog-sp" />}
                <KindIcon kind={rowKind(r)} />
                {href ? <a className="ps-ikey ps-tkey" href={href} target="_blank" rel="noopener noreferrer" title="Open in Orcanos">{r.key}</a>
                  : <span className="ps-ikey ps-tkey">{r.key}</span>}
                {lk && <LockMark tone={lk.tone} title={lk.title} />}
                <button type="button" className={'ps-name' + (located ? ' ps-name--clr' : '')} onClick={() => { if (located) onLocate(''); }}>
                  <span>{r.name}</span>
                  {r.shared > 1 && <span className="ps-badge ps-badge--shared">Shared · {r.shared} products</span>}
                  {r.own_version && r.depth > 0 && <span className="ps-badge ps-badge--own">Own version in this product</span>}
                  {r.branch_added && <span className="ps-badge ps-badge--chg">Added in branch</span>}
                  {r.branch_changed && <span className="ps-badge ps-badge--chg">Changed in branch</span>}
                  {r.missing && <span className="ps-badge ps-badge--miss">Not in the filter</span>}
                  {r.type === 'DMS' && <span className="ps-badge ps-badge--doc">Document · leaf</span>}
                </button>
                {/* Expand / Collapse all: one small chevron on the product row, like the SBOM release grid. */}
                {r.depth === 0 && r.has_kids && (Object.keys(open).filter((k) => open[k]).length > 1
                  ? <button type="button" className="ps-ibtn ps-ibtn--sm" onClick={collapseAll} aria-label="Collapse all" title="Collapse all">
                      <AllChevron up /></button>
                  : <button type="button" className="ps-ibtn ps-ibtn--sm" onClick={expandAll} aria-label="Expand all" title="Expand all">
                      <AllChevron /></button>)}
              </div>
              {shown.map((c) => {
                switch (c.key) {
                  case '__type': return <span key={c.key}>{r.type}</span>;
                  case '__rev': return <span key={c.key}>{r.rev}</span>;
                  case '__status': return <span key={c.key} className={'ps-st ps-st--' + statusTone(r.missing ? 'missing' : r.status)}>{r.missing ? 'Missing' : r.status}</span>;
                  case '__qty': return <span key={c.key}>{r.depth > 0 ? <QtyCell value={r.qty} disabled={!lineEdit} onSave={(n) => setQty(r, n)} /> : ''}</span>;
                  case '__find': return <span key={c.key}>{r.depth > 0 ? <TextCell value={r.find_no} label="Find no." disabled={!lineEdit} onSave={(v) => setText(r, 'find_no', v)} /> : ''}</span>;
                  case '__refdes': return <span key={c.key}>{r.depth > 0 ? <TextCell value={r.ref_des} label="Ref. des." disabled={!lineEdit} onSave={(v) => setText(r, 'ref_des', v)} /> : ''}</span>;
                  case '__unit': return <span key={c.key} className="ps-num">{r.type === 'DMS' ? '—'
                    : canEdit && !inBranch && !r.has_kids && !r.missing && r.cost_field
                      ? <CostCell value={r.unit_cost} onSave={(n) => setCost(r, n)} /> : fmtMoney(r.unit_cost)}</span>;
                  case '__line': return <span key={c.key} className="ps-num">{r.type === 'DMS' ? '—' : fmtMoney(r.depth > 0 ? r.line_cost : r.unit_cost)}</span>;
                  default: {
                    const v = fieldValue(r, c);
                    return <span key={c.key} className="ps-small ps-fcell" title={v}>{v}</span>;
                  }
                }
              })}
              <span className="ps-acts">
                {canAdd && kidsFree && r.type !== 'DMS' && !r.missing && (
                  <IconBtn icon="child" label={`Add a child part under ${r.key}`} onClick={() => setModal({ kind: 'add', row: r })} />
                )}
                {r.depth > 0 && <IconBtn icon="used" label="Where used" onClick={() => whereUsed(r)} />}
                {canEdit && !inBranch && r.depth > 0 && lineFree && (
                  <IconBtn icon="move" label="Move to another assembly" onClick={() => setModal({ kind: 'move', row: r })} />
                )}
                {canEdit && !inBranch && r.own_version && r.depth > 0 && !r.kids_locked && (
                  <IconBtn icon="shared" label="Use the shared version again"
                    onClick={() => { if (window.confirm(`Drop this product's own version of ${r.key} and use the shared one again?`)) run(() => psApi.useShared(root, r.node), `${r.key} uses the shared version again.`); }} />
                )}
                <RowMenu items={[
                  canEdit && !inBranch && !r.lock_inherited && !r.missing && {
                    icon: r.locked ? 'unlock' : 'lock', label: r.locked ? 'Unlock' : 'Lock', onClick: () => toggleLock(r),
                    hint: r.locked ? `Unlock ${r.key}` : `Lock ${r.key}${r.depth > 0 ? ' and everything under it' : ' — the whole structure'} in this product`,
                  },
                  canEdit && canDelete && r.depth > 0 && lineFree && {
                    icon: 'remove', label: 'Remove from this assembly', danger: true, onClick: () => remove(r),
                  },
                ]} />
              </span>
            </div>
          );
        })}
      </div>

      {modal?.kind === 'add' && (
        <AddChildModal parent={modal.row} shared={modal.row.depth === 0 ? 0 : modal.row.shared} inBranch={inBranch}
          onClose={() => setModal(null)} onAdd={add(modal.row)} />
      )}
      {modal?.kind === 'wu' && (
        <WhereUsedModal data={modal.data} onClose={() => setModal(null)}
          onLocate={(r, n) => { setModal(null); onLocate(n, r); }} />
      )}
      {modal?.kind === 'move' && (
        <MoveModal row={modal.row} candidates={moveTargets(modal.row)} onClose={() => setModal(null)}
          onMove={async (target) => {
            await psApi.moveLink(modal.row.link_id, root, target);
            setModal(null);
            showToast(`Moved ${modal.row.key}.`);
            await load();
          }} />
      )}
      {modal?.kind === 'baseline' && (
        <BaselineModal rootLabel={`${rootRow.key} ${rootRow.name}`} onClose={() => setModal(null)}
          onSave={async (b) => {
            const out = await psApi.takeBaseline({ root, ...b });
            setModal(null);
            showToast(`Baseline taken — ${out.parts} parts, ${fmtMoney(out.cost)}.`);
          }} />
      )}
      {modal?.kind === 'branch' && (
        <NewBranchModal rootLabel={`${rootRow.key} ${rootRow.name}`} from={modal.from} onClose={() => setModal(null)}
          onCreate={async (x) => {
            const out = await psApi.addBranch({ root, ...x, parent_id: modal.from?.id || null });
            setModal(null);
            showToast(`Branch “${x.name}” created — you are working in it now.`);
            onBranch(String(out.id));
          }} />
      )}
      {modal?.kind === 'compare' && inBranch && (
        <BranchCompareModal root={root} branch={data.branch} branches={branches} onClose={() => setModal(null)} onAuth={onAuth} />
      )}
      {modal?.kind === 'lock' && (
        <LockModal row={modal.row} unlock={modal.unlock} product={rootRow.key} onClose={() => setModal(null)}
          onConfirm={(comment) => doLock(modal.row, modal.unlock, comment)} />
      )}
      {modal?.kind === 'history' && (
        <HistoryModal root={root} product={`${rootRow.key}${rootRow.name ? ' ' + rootRow.name : ''}`} onClose={() => setModal(null)} onAuth={onAuth} />
      )}
      {modal?.kind === 'export' && (
        <PsExportModal root={root} branch={branchId} onClose={() => setModal(null)} onAuth={onAuth} />
      )}
      {confirmEl}
    </>
  );
}
